import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { IMAGES } from '../../data/content';
import { scrollToId } from '../../hooks/useLenis';

const EASE = [0.22, 1, 0.36, 1];

const lines = ['Shaping the global', 'future of life sciences', '& healthcare.'];

const MaskLine = ({ children, delay, accent }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${accent ? 'text-gold' : 'text-white'}`}
      initial={{ y: '110%' }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden bg-navy-950"
    >
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0"
      >
        <img
          src={IMAGES.heroDna}
          alt=""
          className="h-full w-full object-cover opacity-40 mix-blend-screen"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/85 to-navy-950/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-transparent to-navy-950/60" />

      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto flex min-h-screen max-w-[1400px] flex-col justify-center px-5 sm:px-8 pt-28"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="h-px w-12 bg-gold" />
          <span className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.32em] text-gold">
            London Biotech International Limited
          </span>
        </motion.div>

        <h1 className="max-w-5xl font-serif text-5xl leading-[1.02] tracking-tight sm:text-6xl lg:text-8xl">
          <MaskLine delay={0.25}>{lines[0]}</MaskLine>
          <MaskLine delay={0.4} accent>
            {lines[1]}
          </MaskLine>
          <MaskLine delay={0.55}>{lines[2]}</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 lg:text-lg"
        >
          A UK-registered special-purpose vehicle delivering world-class life
          sciences and healthcare infrastructure through masterplanning, legal
          rigour and clinical expertise.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
          className="mt-12 flex flex-wrap items-center gap-5"
        >
          <button
            data-testid="hero-get-in-touch"
            onClick={() => scrollToId('contact')}
            className="group inline-flex items-center gap-3 border border-gold bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-navy-950 transition-all duration-300 hover:bg-gold-bright hover:border-gold-bright"
          >
            Get in Touch
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            data-testid="hero-learn-more"
            onClick={() => scrollToId('about')}
            className="inline-flex items-center gap-3 px-2 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-white/80 transition-colors hover:text-white"
          >
            Discover the mandate
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 sm:flex"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">
          Scroll
        </span>
        <span className="h-10 w-px bg-gradient-to-b from-gold to-transparent" />
      </motion.div>
    </section>
  );
};
